export const GUATEMALA_TZ = 'America/Guatemala';
const GUATEMALA_UTC_OFFSET_HOURS = 6;

/** YYYY-MM-DD in Guatemala local calendar */
export function formatGuatemalaDate(date = new Date()) {
  return date.toLocaleDateString('en-CA', { timeZone: GUATEMALA_TZ });
}

/** Add days to a YYYY-MM-DD string (Guatemala calendar). */
export function addDaysToDateString(dateStr, days) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const utcNoon = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0, 0));
  return formatGuatemalaDate(utcNoon);
}

/** Midnight at the start of a Guatemala calendar day, as UTC ISO string. */
export function guatemalaDayStartUtcIso(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(
    Date.UTC(year, month - 1, day, GUATEMALA_UTC_OFFSET_HOURS, 0, 0, 0)
  ).toISOString();
}

/** Midnight at the start of the next Guatemala calendar day, as UTC ISO string. */
export function guatemalaDayEndExclusiveUtcIso(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(
    Date.UTC(year, month - 1, day + 1, GUATEMALA_UTC_OFFSET_HOURS, 0, 0, 0)
  ).toISOString();
}

/**
 * PocketBase filter for `fecha` using Guatemala local calendar days.
 * - Start uses YYYY-MM-DD so date-only records (midnight UTC) match.
 * - End uses Guatemala midnight in UTC so evening orders (next UTC date) match.
 */
export function buildFechaRangeFilter(startDate, endDate) {
  const filters = [];

  if (startDate) {
    filters.push(`fecha >= "${startDate}"`);
  }
  if (endDate) {
    filters.push(`fecha < "${guatemalaDayEndExclusiveUtcIso(endDate)}"`);
  }

  return filters;
}

export function getDefaultDashboardDateRange() {
  const todayStr = formatGuatemalaDate();
  // Créditos/mesa: pedidos pueden quedar pendientes varios días (alineado al sync Maguen ~60d)
  const startDateStr = addDaysToDateString(todayStr, -60);
  const yesterdayStr = addDaysToDateString(todayStr, -1);
  return { todayStr, yesterdayStr, startDateStr, endDateStr: todayStr };
}

/** First day of the current month in Guatemala (YYYY-MM-DD). */
export function getGuatemalaMonthStartDate(date = new Date()) {
  const todayStr = formatGuatemalaDate(date);
  return `${todayStr.slice(0, 8)}01`;
}

export function getTimelineDefaultDateRange() {
  const endDateStr = formatGuatemalaDate();
  const startDateStr = getGuatemalaMonthStartDate();
  return { startDateStr, endDateStr };
}

export function buildFechaRangeFilterExpression(startDate, endDate) {
  return buildFechaRangeFilter(startDate, endDate).join(' && ');
}

/** Client-side check mirroring PocketBase fecha range filters. */
export function isFechaInGuatemalaRange(fechaValue, startDateStr, endDateStr) {
  if (!fechaValue || !startDateStr || !endDateStr) return false;
  const time = new Date(fechaValue).getTime();
  if (Number.isNaN(time)) return false;
  const startMs = Date.parse(`${startDateStr}T00:00:00.000Z`);
  const endMs = Date.parse(guatemalaDayEndExclusiveUtcIso(endDateStr));
  return time >= startMs && time < endMs;
}

export function formatGuatemalaDateTime(dateString, { hour12 = true } = {}) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  return date.toLocaleString('es-GT', {
    timeZone: GUATEMALA_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12,
  });
}
